import api from './api';

export interface User {
  id: number;
  name: string;
  email: string;
}

export interface EventComment {
  id: number;
  content: string;
  is_internal: boolean;
  author_id: number;
  author_name: string;
  author_email: string;
  created_at: string;
  updated_at?: string;
}

export interface EventCommentCreate {
  content: string;
  is_internal?: boolean;
}

export interface Event {
  id: number;
  title: string;
  description: string;
  event_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'new' | 'processing' | 'analyzed' | 'validated' | 'false_positive' | 'resolved' | 'ignored';
  source: string;
  source_url?: string;
  confidence_score: number;
  risk_score: number;
  tags?: string[];
  event_metadata?: any;
  created_at: string;
  updated_at?: string;
  occurred_at?: string;
  assigned_to?: User;
}

export interface EventFilters {
  skip?: number;
  limit?: number;
  severity?: string;
  status?: string;
  event_type?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
}

export const eventsService = {
  // Listar eventos com filtros
  async getEvents(filters: EventFilters = {}): Promise<Event[]> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/api/v2/events/?${params.toString()}`);
    return response.data;
  },

  // Obter eventos recentes
  async getRecentEvents(limit: number = 10, hours: number = 24): Promise<Event[]> {
    const response = await api.get(`/api/v2/events/recent?limit=${limit}&hours=${hours}`);
    return response.data;
  },

  // Obter detalhes de um evento específico
  async getEvent(eventId: number): Promise<Event> {
    const response = await api.get(`/api/v2/events/${eventId}`);
    return response.data;
  },

  // Atualizar status de um evento
  async updateEventStatus(eventId: number, status: string): Promise<void> {
    await api.put(`/api/v2/events/${eventId}/status`, { status });
  },

  async createTicketFromEvent(eventId: number, options?: { priority?: string; category?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (options?.priority) params.append('priority', options.priority);
    if (options?.category) params.append('category', options.category);
    const response = await api.post(`/api/v2/events/${eventId}/create-ticket${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  },

  // Obter estatísticas de eventoss
  async getEventStats(): Promise<{
    total: number;
    by_severity: Record<string, number>;
    by_status: Record<string, number>;
    by_type: Record<string, number>;
  }> {
    // Use the optimized backend endpoint for statistics
    const response = await api.get('/api/v2/events/stats');
    return response.data;
  },

  // Obter comentários de um evento
  async getEventComments(eventId: number): Promise<EventComment[]> {
    const response = await api.get(`/api/v2/events/${eventId}/comments`);
    return response.data;
  },

  // Criar um novo comentário para um evento
  async createEventComment(eventId: number, commentData: EventCommentCreate): Promise<EventComment> {
    const response = await api.post(`/api/v2/events/${eventId}/comments`, commentData);
    return response.data;
  },

  async getPhishingStats(days: number = 30): Promise<{
    total_events: number;
    high_risk_events: number;
    medium_risk_events: number;
    low_risk_events: number;
    recent_events_24h: number;
    top_target_domains: { domain: string; count: number }[];
  }> {
    const response = await api.get(`/api/v2/phishing/stats?days=${days}`);
    return response.data;
  },
};
